/**
 * 発注登録モーダル
 *
 * 設計書 05-frontend-design.md §5.3 整合。
 * 現場/材料種別/数量/着荷予定日/備考のフォームを表示し、`createPurchaseOrder` API を呼ぶ。
 */
import { useState } from 'react';
import { Modal, Form, Select, InputNumber, DatePicker, Input, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { logger } from '@/shared/lib/logger';
import { useIsFormSubmittable } from '@/shared/hooks/useIsFormSubmittable';
import { createPurchaseOrder } from '../services/purchaseOrderApiService';
import type {
  CreatePurchaseOrderRequest,
  MaterialTypeOption,
  PurchaseOrderViewModel,
  SiteOption,
} from '../types/purchaseOrder';

const { TextArea } = Input;

const ORDER_NOTE_TEXTAREA_ROWS = 3;
const ISO_DATE_FORMAT = 'YYYY-MM-DD';
/** 発注数量の最小値(1以上の整数) */
const ORDERED_QUANTITY_MIN = 1;

interface FormValues {
  readonly siteId: number;
  readonly materialTypeId: number;
  readonly orderedQuantity: number;
  readonly expectedArrivalDate: Dayjs;
  readonly orderNote?: string;
}

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: (params: { readonly created: PurchaseOrderViewModel }) => void;
  /** 現場の選択肢(MCM/DEV: 全現場、SITE_*: 自サイト1件のみ) */
  readonly siteOptions: readonly SiteOption[];
  /** isActive=true の材料種別 */
  readonly materialTypeOptions: readonly MaterialTypeOption[];
  /** SITE_* ロール時に編集不可で固定する siteId */
  readonly fixedSiteId: number | null;
}

/**
 * 着荷予定日として無効な日付(今日より過去)を判定
 */
const isPastDate = (candidate: Dayjs): boolean => candidate.isBefore(dayjs(), 'day');

export function PurchaseOrderRegistrationModal({
  open,
  onClose,
  onCreated,
  siteOptions,
  materialTypeOptions,
  fixedSiteId,
}: Props): React.ReactElement {
  const [form] = Form.useForm<FormValues>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isSubmittable = useIsFormSubmittable({ form, isFormVisible: open });

  const handleSubmit = async (values: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const request: CreatePurchaseOrderRequest = {
        siteId: values.siteId,
        materialTypeId: values.materialTypeId,
        orderedQuantity: values.orderedQuantity,
        expectedArrivalDate: values.expectedArrivalDate.format(ISO_DATE_FORMAT),
        orderNote: values.orderNote ?? null,
      };
      const created = await createPurchaseOrder({ request });
      message.success('発注を登録しました');
      form.resetFields();
      onCreated({ created });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '発注登録に失敗しました';
      message.error(errorMessage);
      logger.captureError(error, 'PurchaseOrderRegistrationModal.handleSubmit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="新規発注登録"
      okText="登録"
      cancelText="キャンセル"
      confirmLoading={isSubmitting}
      okButtonProps={{ disabled: !isSubmittable }}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          siteId: fixedSiteId ?? undefined,
        }}
      >
        <Form.Item
          name="siteId"
          label="現場"
          rules={[{ required: true, message: '現場を選択してください' }]}
        >
          <Select
            options={[...siteOptions]}
            disabled={fixedSiteId !== null}
            placeholder="現場を選択"
          />
        </Form.Item>

        <Form.Item
          name="materialTypeId"
          label="材料種別"
          rules={[{ required: true, message: '材料種別を選択してください' }]}
        >
          <Select options={[...materialTypeOptions]} placeholder="材料種別を選択" />
        </Form.Item>

        <Form.Item
          name="orderedQuantity"
          label="発注数量"
          rules={[
            { required: true, message: '発注数量を入力してください' },
            {
              type: 'integer',
              min: ORDERED_QUANTITY_MIN,
              message: '発注数量は1以上の整数で入力してください',
            },
          ]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="expectedArrivalDate"
          label="入庫予定日"
          rules={[{ required: true, message: '入庫予定日を選択してください' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            disabledDate={isPastDate}
            format={ISO_DATE_FORMAT}
          />
        </Form.Item>

        <Form.Item name="orderNote" label="備考(任意)">
          <TextArea rows={ORDER_NOTE_TEXTAREA_ROWS} placeholder="備考を入力" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
